import {
  createReviewPayeeState,
  getReviewPayeeDisplayName,
  ReviewPayeeInput,
  ReviewPayeeState,
  ReviewPayeeValidation,
  validateReviewPayee,
} from '@/modules/receipt/review/review-payee-state'

interface ReviewPayeeReceiptData {
  payee?: string | null
  reviewPayee?: ReviewPayeeInput | null
}

interface PreviousDocumentInvoiceDetails {
  reviewPayee?: ReviewPayeeInput | null
}

interface ReviewPayeePersistence {
  accountHolder: string | null | undefined
  reviewPayee?: ReviewPayeeState
}

export const validateReviewPayeeForSave = (
  data: ReviewPayeeReceiptData,
): ReviewPayeeValidation => {
  if (!data.reviewPayee) {
    return { isValid: false, errors: { companyName: 'required' } }
  }

  return validateReviewPayee(createReviewPayeeState(data.reviewPayee))
}

// FLAG:useDocuAi — the "review again" dialog's answer to the one question the server cannot
// decide: the reviewer renamed the payee of an invoice that is already linked to a master record.
//   update — it was a typo; correct the linked record (the save path then renames it)
//   create — it is a DIFFERENT company; drop the link so a new partner is created
//   keep   — leave the master record alone and stay linked to it
// null (no divergence, or a first-time review) leaves the snapshot exactly as the form built it.
export type PartyResolution = 'update' | 'create' | 'keep'

export const applyPartyResolution = (
  reviewPayee: ReviewPayeeState,
  resolution?: PartyResolution | null,
): ReviewPayeeState => {
  switch (resolution) {
    case 'update':
      return { ...reviewPayee, updateDirectory: true }
    case 'create':
      return { ...reviewPayee, directoryRef: null, updateDirectory: false }
    case 'keep':
      return { ...reviewPayee, updateDirectory: false }
    default:
      return reviewPayee
  }
}

export const buildReviewPayeePersistence = (
  data: ReviewPayeeReceiptData,
  previousDetails: PreviousDocumentInvoiceDetails = {},
  partyResolution: PartyResolution | null = null,
): ReviewPayeePersistence => {
  const input = data.reviewPayee ?? previousDetails.reviewPayee
  if (!input) {
    return { accountHolder: data.payee }
  }

  const reviewPayee = applyPartyResolution(createReviewPayeeState(input), partyResolution)
  return {
    accountHolder: getReviewPayeeDisplayName(reviewPayee),
    reviewPayee,
  }
}
